import { useEffect, useState } from 'react';
import { abrirNoSafari, abrirNoChrome, copiarLinkAtual } from '@/lib/instalar';
import { ehInstagram } from '@/lib/navegador';
import PrimaryButton from '@/components/ui/PrimaryButton';

interface FolhaAbrirNavegadorProps {
  aoFechar: () => void;
  alvo: 'ios' | 'android';
}

// Folha de tela cheia "Para instalar o app, abra no Safari/Chrome" — para
// quando a pessoa está no iPhone fora do Safari (Instagram, Facebook, WhatsApp,
// Chrome…) ou no Android dentro de outro app. A compra por aqui continua
// liberada: só não dá para instalar o app.
export default function FolhaAbrirNavegador({ aoFechar, alvo }: FolhaAbrirNavegadorProps) {
  const [planoB, setPlanoB] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const nome = alvo === 'ios' ? 'Safari' : 'Chrome';

  // Trava a rolagem do que está atrás enquanto a folha está aberta.
  useEffect(() => {
    const antes = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = antes;
    };
  }, []);

  function abrir() {
    if (alvo === 'ios') abrirNoSafari();
    else abrirNoChrome();
    // Se 1,5 s depois ainda estiver aqui, o atalho não funcionou: mostra o plano B.
    window.setTimeout(() => setPlanoB(true), 1500);
  }

  async function copiar() {
    const ok = await copiarLinkAtual();
    if (ok) {
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 3000);
    }
  }

  const passos =
    alvo === 'ios'
      ? [
          { icone: 'ri-more-2-fill', texto: 'Toque em ⋯ ou em Compartilhar.' },
          {
            icone: 'ri-external-link-line',
            texto: 'Escolha “Abrir no Safari” ou “Abrir no navegador”.',
          },
          { icone: 'ri-links-line', texto: 'Ou abra o Safari e cole o link.' },
        ]
      : [
          { icone: 'ri-more-2-fill', texto: 'Toque em ⋯ no canto de cima.' },
          {
            icone: 'ri-external-link-line',
            texto: 'Escolha “Abrir no Chrome” ou “Abrir no navegador”.',
          },
          { icone: 'ri-links-line', texto: 'Ou abra o Chrome e cole o link.' },
        ];

  return (
    <div className="fixed inset-0 z-[98] flex flex-col bg-background-50">
      <div
        className="animate-subir flex flex-1 flex-col overflow-y-auto px-5"
        style={{
          paddingTop: 'max(1.25rem, env(safe-area-inset-top))',
          paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))',
        }}
      >
        <div className="mx-auto w-full max-w-[480px]">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={aoFechar}
              aria-label="Fechar"
              className="aperta flex h-10 w-10 cursor-pointer items-center justify-center rounded-full text-foreground-400 transition-colors hover:bg-background-100"
            >
              <i className="ri-close-line text-2xl" aria-hidden />
            </button>
          </div>

          <div className="mt-2 flex flex-col items-center text-center">
            <span className="flex h-16 w-16 items-center justify-center text-primary-500">
              <i className="ri-external-link-line text-[44px]" aria-hidden />
            </span>
            <h2 className="mt-3 text-[22px] font-extrabold leading-tight text-foreground-950">
              Para instalar o app, abra no {nome}
            </h2>
            <p className="mt-2 max-w-sm text-[15px] leading-relaxed text-foreground-600">
              Aqui dentro não dá para colocar o Se Liga na tela do celular. Você pode comprar por
              aqui mesmo — só não dá para instalar.
            </p>
          </div>

          <PrimaryButton
            cheio
            className="mt-6"
            icone="ri-external-link-line"
            onClick={abrir}
          >
            Abrir no {nome}
          </PrimaryButton>

          {planoB && (
            <div className="app-entra mt-4 rounded-[18px] border border-background-200 bg-background-100 p-4">
              <p className="text-[15px] font-bold text-foreground-950">Não abriu?</p>
              <ol className="mt-3 space-y-2.5">
                {passos.map((p, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2.5 text-[15px] leading-relaxed text-foreground-700"
                  >
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center text-primary-600">
                      <i className={`${p.icone} text-lg`} aria-hidden />
                    </span>
                    <span className="pt-0.5">{p.texto}</span>
                  </li>
                ))}
              </ol>
              <button
                type="button"
                onClick={copiar}
                className="aperta mt-4 flex h-12 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[14px] border border-primary-200 bg-background-50 px-4 text-[15px] font-bold text-primary-700 transition-colors hover:bg-primary-50"
              >
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className={copiado ? 'ri-check-line' : 'ri-file-copy-line'} aria-hidden />
                </span>
                {copiado ? 'Link copiado' : 'Copiar link'}
              </button>
            </div>
          )}

          {ehInstagram && alvo === 'ios' && (
            <p className="mt-4 rounded-[14px] bg-warning-50 px-3 py-2 text-[13px] leading-snug text-warning-800">
              No Instagram: toque nos <b>⋯</b> no canto de cima ›{' '}
              <b>Abrir no navegador externo</b>.
            </p>
          )}
        </div>
      </div>

      <div
        className="border-t border-background-200 px-5 py-2"
        style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}
      >
        <button
          type="button"
          onClick={aoFechar}
          className="aperta mx-auto block cursor-pointer whitespace-nowrap py-2.5 text-[15px] font-semibold text-foreground-500 transition-colors hover:text-foreground-700"
        >
          Agora não
        </button>
      </div>
    </div>
  );
}